<template>
	<v-container fluid>
		<v-dialog v-model="isDialogOpen" max-width="90%" :retain-focus="false">
			<!-- Overlay para mostrar el loader -->
			<v-overlay v-if="isLoading">
				<v-progress-circular indeterminate size="32" color="primary"></v-progress-circular>
			</v-overlay>
			<v-card>
				<v-card-title>
					<v-panel>
						<span style="font-size: 20px;">{{ $t('wkt.admin.assessment.participant-window.title') }}</span>
					</v-panel>
				</v-card-title>
				<v-card-text @wheel="onWheel">
					<v-row class="align-center justify-space-between mb-4">
						<v-col cols="12" sm="6" md="4" lg="3" class="d-flex align-center">
							<!-- Campo de búsqueda -->
							<v-text-field v-model="search"
								:label="$t('wkt.admin.assessment.participant-window.search_employee')" hide-details
								:clearable="true" @click:clear="searchTimeOut()" @keyup="searchTimeOut()"
								style="min-width: 200px;" />

							<!-- Botón para abrir/cerrar los filtros -->
							<v-btn text color="primary" @click="showFilters = !showFilters">
								<v-icon left>{{ showFilters ? 'filter_list' : 'filter_list' }}</v-icon>
								{{ showFilters ? $t('wkt.admin.common.hide-filter') :
									$t('wkt.admin.common.advanced-filter') }}
							</v-btn>
						</v-col>
						<!-- Botón de acciones -->
						<v-col cols="auto">
							<Actions :actions="actionItems" />
						</v-col>
					</v-row>
					<!-- Contenedor de filtros (visible solo cuando showFilters es true) -->
					<v-expand-transition>
						<div v-if="showFilters" class="mt-2 mb-4" style="max-height: 300px; overflow-y: auto;">
							<v-row>
								<!-- Filtro por Evaluador -->
								<v-col cols="12" sm="6" md="4" lg="2">
									<v-combobox v-model="filters.evaluator" :items="evaluatorOptions" item-text="name"
										item-value="id" :label= "this.$t('wkt.admin.assessments.edit-window.table.headers.evaluator')" multiple clearable @change="getItems" dense
										hide-details />
								</v-col>
								<!-- Filtro por Autoevaluación -->
								<v-col cols="12" sm="6" md="4" lg="2">
									<v-combobox v-model="filters.selfAssessment" :items="[
										{ value: '1', text: this.$t('wkt.admin.assessments.edit-window.table.status_pending') },
										{ value: '0', text: this.$t('wkt.admin.survey.stats.question-overview.table.headers.answered') }

									]" :label= "this.$t('wkt.admin.assessment-window.assessment_type1')" multiple clearable @change="getItems" dense hide-details />
								</v-col>
								<!-- Filtro por Evaluación Final -->
								<v-col cols="12" sm="6" md="4" lg="2">
									<v-combobox v-model="filters.finalAssessment" :items="[
										{ value: '1', text: this.$t('wkt.admin.assessments.edit-window.table.status_pending') },
										{ value: '0', text: this.$t('wkt.admin.survey.stats.question-overview.table.headers.answered') }
									]" :label= "this.$t('wkt.admin.assessments.edit-window.table.headers.phase2')" multiple clearable @change="getItems" dense hide-details />
								</v-col>
								<!-- Filtro por Estado -->
								<v-col cols="12" sm="6" md="4" lg="2">
									<v-combobox v-model="filters.status" :items="statusOptions" multiple :label= "this.$t('wkt.admin.assessments.edit-window.table.headers.status')"
										clearable @change="getItems" dense hide-details />
								</v-col>
							</v-row>
						</div>
					</v-expand-transition>
					<v-data-table :headers="headers" :items="items" :loading="apiLoading" style="margin-bottom: 50px;"
						@click:row="addParticipantsDialogOpen" :loading-text="$t('wkt.admin.common.table.loading')"
						:no-data-text="$t('wkt.admin.common.table.no_data')" :items-per-page="-1" hide-default-footer
						:sort-by="sortBy" :sort-desc="sortDesc" @update:sort-by="updateSort"
						@update:sort-desc="updateSortDesc" class="participantsTable">
						<!-- Columna personalizada: Empleado -->
						<template v-slot:[`item.employee`]="{ item }">
							<v-row class="align-center">
								<!-- Avatar -->
								<v-col cols="2" class="d-flex justify-center">
									<v-avatar v-if="getProfileAssetUrl(item.employeeProfilePicture)" style="border-radius: 0;">
										<v-img :src="getProfileAssetUrl(item.employeeProfilePicture)" max-width="38"
											max-height="38"
											:alt="`${item.employeeFirstname} ${item.employeeLastname}`" />
									</v-avatar>
								</v-col>
								<!-- Información del empleado -->
								<v-col cols="10">
									<div :style="!item.employeeEnabled ? 'color: red !important;' : ''">
										<strong>
											{{ item.employeeFirstname }} {{ item.employeeLastname }}
											<span v-if="!item.employeeEnabled">({{
												$t('wkt.admin.edit-employee-window.disabled') }})</span>
										</strong>
										<div :style="!item.employeeEnabled ? 'color: red !important;' : ''"
											:class="{ 'text--secondary': item.employeeEnabled }">
											{{ item.employeeDepartmentName }} - {{ item.employeeWorkplaceName }}
										</div>
									</div>
								</v-col>
							</v-row>
						</template>

						<!-- Columna personalizada: Fase 1 -->
						<template v-slot:[`item.phase1`]="{ item }">
							<div v-if="shouldShowPhase1Column">
								<div>
									<div v-if="item.phase1CompletionDate" class="clickable underline indigo--text"
										@click.stop="showReview(item, 1)"
										style="text-decoration: underline; cursor: pointer;">
										{{ formatDate(item.phase1CompletionDate) }}
									</div>
									<div v-else class="grey--text lighten-1">
										{{ statusPending }}
									</div>
								</div>
								<div class="grey--text lighten-1 form-name">
									{{ item.phase1FormName }}
								</div>
							</div>
						</template>
						<!-- Columna personalizada: Evaluador -->
						<template v-slot:[`item.evaluator`]="{ item }" v-if="shouldShowEvaluatorColumn">
							<v-row no-gutters align="center" class="flex-nowrap">
								<!-- Avatar o Icono de advertencia -->
								<v-col cols="2" class="d-flex justify-center pr-2">
									<v-avatar style="border-radius: 0;">
										<v-icon v-if="!item.evaluatorId" color="warning">warning</v-icon>
										<v-img v-else-if="getProfileAssetUrl(item.evaluatorProfilePicture)"
											:src="getProfileAssetUrl(item.evaluatorProfilePicture)"
											:alt="`${item.evaluatorFirstname} ${item.evaluatorLastname}`" max-width="38"
											max-height="38" />
										<v-icon v-else>mdi-account</v-icon>
									</v-avatar>
								</v-col>
								<!-- Información del Evaluador -->
								<v-col cols="10">
									<div v-if="item.evaluatorId"
										:style="!item.evaluatorEnabled ? 'color: red !important;' : ''">
										<strong class="text-truncate">
											{{ item.evaluatorFirstname }} {{ item.evaluatorLastname }}
											<span v-if="!item.evaluatorEnabled">({{
												$t('wkt.admin.edit-employee-window.disabled') }})</span>
										</strong>
										<div :style="!item.evaluatorEnabled ? 'color: red !important;' : ''"
											:class="{ 'text--secondary': item.evaluatorEnabled }" class="text-truncate">
											{{ getDepartmentWorkplace(item.evaluatorDepartmentName,
												item.evaluatorWorkplaceName) }}
										</div>
									</div>
									<div v-else>
									</div>
								</v-col>
							</v-row>
						</template>
						<!-- Columna personalizada: Fase 2 -->
						<template v-slot:[`item.phase2`]="{ item }">
							<div v-if="shouldShowEvaluatorColumn">
								<div v-if="item.phase2CompletionDate" class="clickable underline indigo--text"
									@click.stop="showReview(item, 2)"
									style="text-decoration: underline; cursor: pointer;">
									{{ formatDate(item.phase2CompletionDate) }}
								</div>
								<div v-else class="grey--text lighten-1">
									{{ statusPending }}
								</div>
								<div class="grey--text lighten-1 form-name">
									{{ item.phase2FormName }}
								</div>
							</div>
						</template>

						<!-- Columna personalizada: Estado -->
						<template v-slot:[`item.reviewstatus`]="{ item }">
							<span :class="statusClass(item.reviewStatus)">
								{{ getStatusDescription(item.reviewStatus) }}
							</span>
						</template>

						<!-- Columna personalizada: Calificación -->
						<template v-slot:[`item.reviewScore`]="{ item }">
							<span>{{ item.reviewScore }}</span>
						</template>
					</v-data-table>
				</v-card-text>
				<!-- Botón flotante FAB dentro del diálogo -->
				<v-tooltip left :disabled="isReadOnly">
					<template v-slot:activator="{ on, attrs }">
						<v-btn dark absolute bottom right fab color="#185EA9" v-bind="attrs" v-on="on"
							:style="{ bottom: `${fabOffset}px`, right: '16px' }" @click="openTargetManagerDialog()" :disabled="isReadOnly">
							<v-icon dark>add</v-icon>
						</v-btn>
					</template>
					<span>{{ $t("wkt.admin.assessment.participant-window.wizard_text") }}</span>
				</v-tooltip>
				<v-divider class="my-2" style="border-width: 1px;"></v-divider>
				<v-card-actions class="justify-start">
					<v-spacer></v-spacer>
					<v-btn text class="grey--text text--darken-1 custom-btn" @click="closeDialog">
						<v-icon left small>mdi-close</v-icon>
						{{ $t('wkt.admin.common.dialog-buttons.close') }}
					</v-btn>
				</v-card-actions>
			</v-card>
		</v-dialog>
		<!-- Diálogo de añadir participantes -->
		<v-dialog v-model="participantsDialog" max-width="600px">
			<v-card>
				<v-card-title v-if="assessment">
					<span class="headline">{{ $t(assessment.assessmentName) }}</span>
				</v-card-title>
				<v-card-text>
					<!-- Aquí va el contenido del diálogo de participantes -->
					<v-list>
						<v-list-item v-for="(participant, index) in participantsData" :key="index">
							<v-list-item-content>
								<v-list-item-title>{{ participant.name }}</v-list-item-title>
								<v-list-item-subtitle>{{ participant.email }}</v-list-item-subtitle>
							</v-list-item-content>
						</v-list-item>
					</v-list>
				</v-card-text>
				<v-card-actions>
					<v-spacer></v-spacer>
					<v-btn color="blue darken-1" text @click="participantsDialog = false">
						{{ $t('wkt.admin.common.dialog-buttons.close') }}
					</v-btn>
					<v-btn color="blue darken-1" text>
						{{ $t('wkt.admin.common.dialog-buttons.save') }}
					</v-btn>
				</v-card-actions>
			</v-card>
		</v-dialog>
		<!-- Diálogo estadísticas -->
		<v-dialog v-model="statisticsDialog" max-width="80%">
			<v-card>
				<v-card-title>
					<span class="headline">{{ $t(assessment.assessmentName) }}</span>
				</v-card-title>
				<v-card-text>
					<!-- Aquí va el contenido del diálogo de estadisticas -->
					<v-tabs v-model="tab" v-if="!loading">
						<v-tab href="#phase1" @click="refreshPhase1"
							v-show="assessment.assessmentType == 0 || assessment.assessmentType == 2">{{
								$t('wkt.admin.survey.stats.question-overview.tab.self-appraisal') }}</v-tab>
						<v-tab href="#phase2" @click="refreshPhase2"
							v-show="assessment.assessmentType == 1 || assessment.assessmentType == 2">{{
								$t('wkt.admin.survey.stats.question-overview.tab.final-evaluation') }}</v-tab>
					</v-tabs>

					<v-tabs-items v-if="!loading" v-model="tab">
						<v-tab-item value="phase1">
							<QuestionOverview ref="questionOverview1" />
						</v-tab-item>
						<v-tab-item value="phase2">
							<QuestionOverview ref="questionOverview2" />
						</v-tab-item>
					</v-tabs-items>
				</v-card-text>
				<v-card-actions>
					<v-spacer></v-spacer>
					<v-btn text class="grey--text text--darken-1 custom-btn" @click=closeStatitiscsDialog()>
						{{ $t('wkt.admin.common.dialog-buttons.close') }}
					</v-btn>
				</v-card-actions>
			</v-card>
		</v-dialog>
		<!--FormPreviewDialog-->
		<template>
			<v-dialog v-model="formPreviewDialog" max-width="80%" height="85%" ref="formPreviewDialog"
				@input="onDialogStateChange"> <v-card style="display: flex; flex-direction: column; height: 90vh;">
					<v-card-title class="py-2">
						<span class="text-h5">{{ formPreviewDialogTitle }}</span>
					</v-card-title>

					<v-card-text style="flex-grow: 1; position: relative; padding: 0;">
						<!-- Iframe -->
						<iframe v-if="iframeUrl" ref="externalContentFrame" :src="iframeUrl"
							style="top: 0; left: 0; width: 100%; height: 100%; border: 0;" @load="onIframeLoad"
							@error="onIframeError"></iframe>
					</v-card-text>

					<template>
						<div>
							<v-divider class="my-2" style="border-width: 1px;"></v-divider>
							<v-card-actions class="py-2">
								<v-btn color="indigo" text @click="printContent">
									<v-icon left>print</v-icon>
									{{ $t('wkt.admin.common.print') }}
								</v-btn>
								<v-spacer></v-spacer>
								<v-btn color="grey darken-1" text @click="closeFormPreviewDialog">
									{{ $t('wkt.admin.common.dialog-buttons.cancel') }}
								</v-btn>
							</v-card-actions>
						</div>
					</template>
				</v-card>
			</v-dialog>
		</template>

		<!-- Diálogo AddParticipantsDialog -->
		<AddParticipantsDialog ref="addParticipantsDialogOpen" @participant-deleted="getItems"
			@assessment-review-updated="getItems" />
		<!--Dialogo TargetManager-->
		<TargetManagerDialog ref="dlgTargetManager" :filterByProfile="true" />
		<v-snackbar v-model="showAlert" :timeout="alertTimeout" :color="alertColor">
			<span style="color: #ffffff;">{{ alertText }}</span>
			<v-btn text @click="showAlert = false">
				<span style="color: #ffffff;">{{ $t('wkt.admin.common.dialog-buttons.close') }}</span>
			</v-btn>
		</v-snackbar>
	</v-container>
</template>

<script>
import api from '../../../api/worktime';
import BaseModule from '../../common/BaseModule.js'
import QuestionOverview from "../stats/QuestionOverview";
import AddParticipantsDialog from "./AddParticipantsDialog.vue";
import TargetManagerDialog from './TargetManagerDialog.vue';
import gwt from '../../../api/gwt.js';
import Actions from '../../common/Actions.vue';
import { Constants } from "../../common/Constants.js";

const ASSESSMENT_STATUS = {
	ASSESSMENT_STATUS_PENDING: 0,
	ASSESSMENT_STATUS_IN_PROGRESS: 1,
	ASSESSMENT_STATUS_FINISHED: 2,
	ASSESSMENT_STATUS_CANCELED: 3
};

const ASSESSMENT_TYPE = {
	ASSESSMENT_TYPE_PHASE1_ONLY: 0,
	ASSESSMENT_TYPE_PHASE2_ONLY: 1,
	ASSESSMENT_TYPE_PHASE1_AND_PHASE2: 2
};

export default {
	name: 'ParticipantsDialog',
	mixins: [BaseModule],
	components: {
		QuestionOverview, AddParticipantsDialog, TargetManagerDialog, Actions
	},
	data() {
		return {
			// Constantes y opciones
			ASSESSMENT_STATUS,
			ASSESSMENT_TYPE,
			newEvaluators: [],
			statusOptions: [

				{ value: 0, text: this.$t('wkt.admin.assessment.status_pending') },
				{ value: 1, text: this.$t('wkt.admin.assessment.status_in_progress') },
				{ value: 2, text: this.$t('wkt.admin.assessment.status_finished') },
				{ value: 3, text: this.$t('wkt.admin.assessment.status_canceled') },
			],
			statusPending: this.$t('wkt.admin.assessments.edit-window.table.status_pending'),

			// Datos principales
			assessment: {
				assessmentType: -1
			},
			items: [],
			totalItems: 0,

			// Opciones de filtrado y búsqueda
			filters: {
				evaluator: [],
				status: [],
			},
			search: '',
			showFilters: false,
			filterTimer: null,

			// Opciones de paginación y ordenamiento
			options: {
				page: 1,
				itemsPerPage: 1000,
				sortBy: [],
				sortDesc: [],
			},
			sortBy: null,
			sortDesc: false,

			// Estados de carga
			apiLoading: false,
			isLoading: false,
			loading: false,

			// Opciones de selección
			employeeOptions: [],
			evaluatorOptions: [],

			// Elementos de acción			
			actionItems: [],

			// Configuración de la tabla
			headers: [
				{
					text: this.$t('wkt.admin.assessment.edit-window.table.headers.employee'),
					value: 'employee',
					width: '300px',
					sortable: true,
				},
				{
					text: this.$t('wkt.admin.assessments.table.headers.phase1'),
					value: 'phase1',
					width: '150px',
					sortable: true,
				},
				{
					text: this.$t('wkt.admin.assessments.edit-window.table.headers.evaluator'),
					value: 'evaluator',
					width: '300px',
					sortable: true,
				},
				{
					text: this.$t('wkt.admin.assessments.table.headers.phase2'),
					value: 'phase2',
					width: '150px',
					sortable: true,
				},
				{
					text: this.$t('wkt.admin.assessments.edit-window.table.headers.status'),
					value: 'reviewstatus',
					width: '150px',
					sortable: true,
				},
				{
					text: this.$t('wkt.admin.assessments.edit-window.table.headers.score'),
					value: 'reviewscore',
					width: '150px',
					sortable: true,
				},
			],

			// Estados de diálogo
			formPreviewDialog: false,
			formPreviewDialogTitle: '',
			statisticsDialog: false,
			participantsDialog: false,
			isDialogOpen: false,

			// Alertas
			showAlert: false,
			alertColor: 'error',
			alertText: '',
			alertTimeout: 3000,

			// Otros
			employeeIds: [],
			callbackId: 'callback_' + Math.random().toString(36).substr(2, 9),
			iframeUrl: '',
			initCompleted: false,
			fabOffset: 80,
			tab: 'phase1',
			participantsData: [],
			filteredItems: [],
			profile: null,
			permissions:null
		};
	},
	computed: {
		shouldShowPhase1Column() {
			return this.assessment.assessmentType === ASSESSMENT_TYPE.ASSESSMENT_TYPE_PHASE1_ONLY ||
				this.assessment.assessmentType === ASSESSMENT_TYPE.ASSESSMENT_TYPE_PHASE1_AND_PHASE2;
		},
		shouldShowEvaluatorColumn() {
			return this.assessment.assessmentType === ASSESSMENT_TYPE.ASSESSMENT_TYPE_PHASE2_ONLY ||
				this.assessment.assessmentType === ASSESSMENT_TYPE.ASSESSMENT_TYPE_PHASE1_AND_PHASE2;
		},
		sessionModuleAssessments() {
		return this.profile?.moduleAssessments;
		},
		userHasAnyAccess() {
		const mp = this.sessionModuleAssessments;
		return !(mp === Constants.DENY_ALL);
		},
		isReadOnly() {
		return (this.sessionModuleAssessments === Constants.ALLOW_READ_ONLY);
		},
	},
	methods: {
		onReveal(payload) {
			try {
				// Inicializar el perfil de sesión
				this.profile = gwt.getSessionProfile();
				
				if (!this.userHasAnyAccess) {
					this.$emit("show-alert", { text: this.$t("wkt.admin.profiles.no_permission"), color: "error", timeout: 3000 });
					return;
				}

				this.resetFilters();
				this.permissions = this.profile.moduleAssessments;
				this.tab = 'phase1';
				this.assessment = typeof payload === 'string' ? JSON.parse(payload) : payload;
				if (!this.assessment || !this.assessment.assessmentType) {
					this.tab = 'phase1';
				}
				this.isDialogOpen = true;
				this.$nextTick(() => {
					this.getItems();
				});
				
				// Resetear actionItems para evitar duplicados
				this.actionItems = [];
				
				// Añade acción, Notificar estado no iniciado
				let phase1StartDate = new Date(this.assessment.phase1StartDate);
				let phase2StartDate = new Date(this.assessment.phase2StartDate);
				let phase1DueDate = new Date(this.assessment.phase1DueDate);
				let phase2DueDate = new Date(this.assessment.phase2DueDate);
				let assessmentType = this.assessment.assessmentType;
				let now = new Date();

				// Mostrar botón de notificación si las fechas están abiertas (futuras)
				let shouldShowNotification = false;
				if (assessmentType == ASSESSMENT_TYPE.ASSESSMENT_TYPE_PHASE1_ONLY && phase1StartDate < now && phase1DueDate > now) {
					shouldShowNotification = true;
				} else if (assessmentType == ASSESSMENT_TYPE.ASSESSMENT_TYPE_PHASE2_ONLY && phase2StartDate < now && phase2DueDate > now) {
					shouldShowNotification = true;
				} else if (assessmentType == ASSESSMENT_TYPE.ASSESSMENT_TYPE_PHASE1_AND_PHASE2 && phase1StartDate < now && phase2DueDate > now) {
					shouldShowNotification = true;
				}

				if (this.permissions > Constants.ALLOW_READ_ONLY && shouldShowNotification) {
					this.actionItems.push({
						text: this.$t('wkt.admin.assessment.participant-window.assesment_notification'),
						icon: "send",
						onClick: () => this.sendNotification(),
					});
				}

				// añade accion estadisticas
				this.actionItems.push(
					{
						text: this.$t('wkt.admin.assessment.participant-window.assessment_stats'),
						icon: "timeline",
						onClick: () => this.openStatisticsDialog(),
					},
				);
				
				// añade accion exportar autoevaluación
				if (this.assessment.assessmentType === ASSESSMENT_TYPE.ASSESSMENT_TYPE_PHASE1_ONLY || 
					this.assessment.assessmentType === ASSESSMENT_TYPE.ASSESSMENT_TYPE_PHASE1_AND_PHASE2
				) {
					this.actionItems.push(
						{
							text: this.$t('wkt.admin.assessment.participant-window.self_assessment_export'), // Exportar autoevaluación
							icon: "apps",
							onClick: () => this.onDownloadAssessmentExcel(ASSESSMENT_TYPE.ASSESSMENT_TYPE_PHASE1_ONLY),
						}
					);
				}

				// añade accion exportar evaluación final
				if (this.assessment.assessmentType === ASSESSMENT_TYPE.ASSESSMENT_TYPE_PHASE2_ONLY || 
					this.assessment.assessmentType === ASSESSMENT_TYPE.ASSESSMENT_TYPE_PHASE1_AND_PHASE2
				) {
					this.actionItems.push(
						{
							text: this.$t('wkt.admin.assessment.participant-window.final_assessment_export'), // Exportar evaluación final
							icon: "apps",
							onClick: () => this.onDownloadAssessmentExcel(ASSESSMENT_TYPE.ASSESSMENT_TYPE_PHASE2_ONLY),
						}
					);
				}
			} catch (error) {
				console.error('Error en onReveal:', error);
			}
		},
		resetFilters() {
			// Resetear los filtros de los comboboxes
			this.filters = {
				evaluator: [],
				selfAssessment: [],
				finalAssessment: [],
				status: []
			};
			// Resetear el campo de búsqueda
			this.search = '';
			// Limpiar los items de la tabla
			this.items = [];
			this.totalItems = 0;
		},
		applyDefinition() {
			
			if (this.assessment.assessmentType === undefined || this.assessment.assessmentType === null) {
				this.tab = 'phase1';
				return;
			}

			if (this.assessment.assessmentType === 0 || this.assessment.assessmentType === 2) {
				this.tab = 'phase1';
				this.refreshPhase1();
			} else {
				this.tab = 'phase2';
				this.refreshPhase2();
			}
		},
		onDialogStateChange(isOpen) {
			if (isOpen && this.iframeUrl === '') {
				this.iframeUrl = `form-viewer.html?review=true&callbackId=${this.callbackId}&lang=es&protected=true`;
			}
		},
		loadSubmissions() {
			this.loading = true;
			let me = this;
			api.getAssessmentSubmissions(me.assessment.id,
				data => {
					me.submissionInfo = data;
					me.assessment = data.assessment;
					me.loading = false;
					me.applyDefinition();
					this.statisticsDialog = true;
					this.isLoading = false;
				},
				err => {
					console.log(err);
					this.isLoading = false;
				});
		},

		onWheel(event) {
			event.stopPropagation()
		},
		refreshPhase1() {
			let me = this;
			setTimeout(function () {
				me.$refs.questionOverview1.refresh({
					currentResponses: me.assessment.phase1Reviews,
					expectedResponses: me.assessment.expectedReviews
				}, {
					survey: me.assessment,
					form: me.submissionInfo.phase1Form,
					submissions: me.submissionInfo.phase1Submissions
				});
			}, 200);
		},
		refreshPhase2() {
			let me = this;
			setTimeout(function () {

				me.$refs.questionOverview2.refresh({
					currentResponses: me.assessment.phase2Reviews,
					expectedResponses: me.assessment.expectedReviews
				}, {
					survey: me.assessment,
					form: me.submissionInfo.phase2Form,
					submissions: me.submissionInfo.phase2Submissions
				});
			}, 200);
		},
		getDepartmentWorkplace(department, workplace) {
			if (department && workplace) {
				return `${department} - ${workplace}`;
			} else if (department) {
				return department;
			} else if (workplace) {
				return workplace;
			}
			return '';
		},
		addParticipantsDialogOpen(currentAssesment) {

			this.isLoading = true;
			api.getAssessmentReviewById(currentAssesment.id,
				data => {
					this.isLoading = false;
					const review = data;
					this.$refs.addParticipantsDialogOpen.open(review, currentAssesment);
				},
				err => {
					this.isLoading = false;
					this.$emit("show-alert", { text: err.message, color: "error", timeout: 3000 });

				});


		},
		openTargetManagerDialog() {
			this.employeeIds = [];

			this.$refs.dlgTargetManager.open(this.assessment.id, null, (employeeIds) => {
				this.employeeIds = employeeIds;
				this.isLoading = true;
				let data = {
					assessmentId: this.assessment.id,
					employees: this.employeeIds.map(id => id.toString()).join(','),
					//Todo Revisar
					target: 0,
				};

				api.storeAssessmentReviews(data,
					(response) => {
						this.isLoading = false;
						this.getItems();
						const message = this.$t('wkt.admin.assessments.wizard-dialog.reviews_stored').replace('%d', response);
						this.doShowAlert({ text: message, color: 'green', timeout: 3000 })
					},
					(error) => {
						this.isLoading = false;
						console.error('Error al almacenar las revisiones de evaluación:', error);
						this.doShowAlert({ text: error.message, color: 'error', timeout: 3000 })
					}
				);
			});
		},
		openFormPreviewDialog(title) {

			this.formPreviewDialogTitle = title;
			this.iframeUrl = `form-viewer.html?review=true&callbackId=${this.callbackId}&lang=es&protected=true`;
			this.formPreviewDialog = true;

			this.$nextTick(() => {
				const instance = this.$refs.externalContentFrame;
				if (instance) {
					instance.onload = () => {
						this.sendIframeData(); // Envía datos al iframe
					};
				}
			});
		},
		sendIframeData() {
			const instance = this.$refs.externalContentFrame;

			if (instance && instance.contentWindow) {
				instance.contentWindow.postMessage(
					{
						type: 'ready',
						definition: api.encodeBase64(this.definition) || '{}',
						lastSubmission: api.encodeBase64(this.lastSubmission) || '{}',
						role: this.role,
						protected: this.isProtected,
						sessionInfo: this.sessionInfo || '{}',
					},
					'*'
				);
			} else {
				console.error('El iframe no está disponible. Datos no enviados.');
			}
		},
		closeFormPreviewDialog() {
			this.formPreviewDialog = false;
			this.iframeUrl = ''
		},
		printContent() {
			this.sendFormAction("print", "{}");
		},
		closeDialog() {
			gwt.fireEvent('RefreshAssesmentEvent');
			this.isDialogOpen = false;
		},
		closeStatitiscsDialog() {
			this.statisticsDialog = false;
		},
		// Configura un tiempo de espera para la búsqueda
		searchTimeOut() {
			if (this.timer) {
				clearTimeout(this.timer);
			}
			this.timer = setTimeout(() => {
				this.options.page = 1;
				this.getItems();
			}, 500);
		},
		async getItems() {
			this.apiLoading = true;
			try {
				const filter = {
					assessmentId: this.assessment?.id,
					offset: (this.options.page - 1) * this.options.itemsPerPage,
					limit: this.options.itemsPerPage,
					filter: this.search?.trim() || '',

					evaluators: this.filters.evaluator && this.filters.evaluator.length > 0 ? this.filters.evaluator.map(e => e.id) : null,
					status: this.filters.status && this.filters.status.length > 0 ? this.filters.status.map(s => s.value) : null,
					selfAssessment: this.filters.selfAssessment && this.filters.selfAssessment.length > 0 ? this.filters.selfAssessment.map(s => s.value) : null,
					finalAssessment: this.filters.finalAssessment && this.filters.finalAssessment.length > 0 ? this.filters.finalAssessment.map(f => f.value) : null,
				};

				await new Promise((resolve, reject) => {
					api.getAssessmentReviewsByAssessmentId(
						filter,
						(data) => {
							this.items = data.entities;
							this.totalItems = data.total;
							// Actualizar evaluatorOptions solo con nuevos evaluadores únicos
							this.newEvaluators = [];
							this.evaluatorOptions = [];
							if (data !== "") {
								this.newEvaluators = data.entities
									.filter(entity =>
										entity.evaluatorId != null &&
										entity.evaluatorFirstname != null &&
										entity.evaluatorLastname != null
									)
									.map(entity => ({
										id: entity.evaluatorId,
										name: `${entity.evaluatorFirstname} ${entity.evaluatorLastname}`
									}));
							}
							this.updateEvaluatorOptions(this.newEvaluators);

							resolve(data);
						},
						(error) => {
							console.error("Error al obtener datos:", error);
							reject(error);
						}
					);
				});
			} catch (error) {
				console.error('Error en getItems:', error);
				this.doShowAlert({ text: error.message, color: 'error', timeout: 3000 });
			} finally {
				this.apiLoading = false;
			}
		},
		formatDate(date) {
			return new Date(date).toLocaleString();
		},
		statusClass(status) {
			switch (status) {
				case 3: return 'red--text';
				case 0: return 'orange--text';
				case 2: return 'blue--text';
				default: return 'grey--text';
			}
		},
		getStatusDescription(status) {
			switch (status) {
				case 0: return this.$t('wkt.admin.assessments.status_description.not_init');
				case 1: return this.$t('wkt.admin.assessments.status_description.on_course');
				case 2: return this.$t('wkt.admin.assessments.status_description.finished');
				case 3: return this.$t('wkt.admin.assessments.status_description.canceled');
				default: return 'N/A';
			}
		},
		showReview(item, phase) {
			this.isLoading = true;
			// Llama al API para obtener los datos necesarios
			api.getFormAssessmentReviewInfo(
				item.id,
				phase,
				(data) => {
					console.log('Datos de revisión recibidos:', data);

					const phaseTitle = phase === 1
						? this.$t('wkt.admin.assessments.common.phase1.name')
						: this.$t('wkt.admin.assessments.common.phase2.name');

					const title = `${phaseTitle} - ${item.employeeLastname}, ${item.employeeFirstname}`;

					// Configura los datos necesarios para el iframe
					this.definition = data.form.definition;
					this.role = phase === 1 ? 'EMPLOYEE' : 'EVALUATOR';
					this.lastSubmission = data.lastSubmission ? data.lastSubmission.submissionData : null;
					this.sessionInfo = '{}';
					this.isProtected = true;

					// Abre el diálogo
					this.openFormPreviewDialog(title);
				},
				(error) => {
					console.error('Error al obtener la revisión:', error);
				}
			);
		},
		sendFormData(info, phase) {
			const role = phase === 1 ? 'EMPLOYEE' : 'EVALUATOR';
			const lastSubmission = info.lastSubmission ? info.lastSubmission.submissionData : null;

			this.setFormDefinition(
				info.form.definition,
				role,
				lastSubmission,
				'{}',
				'true'
			);
		},
		setFormDefinition(definition, role, lastSubmission, sessionInfo, isProtected) {
			const instance = this.$refs.externalContentFrame;
			if (instance && instance.contentWindow) {
				instance.contentWindow.postMessage(
					{
						type: 'ready',
						definition: definition || '{}',
						lastSubmission: lastSubmission || '{}',
						role: role,
						protected: isProtected,
						sessionInfo: sessionInfo,
					},
					'*'
				);
			} else {
				console.error('El iframe no está disponible. Intentando después...');
				// Intenta nuevamente después de un pequeño retraso
				setTimeout(() => {
					this.setFormDefinition(definition, role, lastSubmission, sessionInfo, isProtected);
				}, 100); // Reintenta después de 100ms
			}
		},
		sendFormAction(action, payload) {
			const iframe = this.$refs.externalContentFrame;
			if (iframe && iframe.contentWindow) {
				iframe.contentWindow.postMessage({
					action: action,
					payload: payload || '{}'
				}, '*');
			} else {
				console.error('El iframe no está disponible');
			}
		},
		sendNotification() {
			try {
				let phase = this.assessment.assessmentType;
				
				this.isLoading = true;
				api.notifyPendingReviews(
					this.assessment.id,
					phase,
					(response) => {
						const count = response;
						this.doShowAlert({
							text: this.$t("wkt.admin.assessments.notification_sent_to_not_started_participants" ) + ' ' + '(' + count + ')',
							color: 'green',
							timeout: 3000,
						});
						this.isLoading = false;
					},
					(error) => {
						console.error('Error enviando notificaciones:', error);
						this.doShowAlert({
							text: this.$t("wkt.admin.assessments.error_sending_notifications") + (error.message ? ': ' + error.message : ''),
							color: 'error',
							timeout: 4000,
						});
						this.isLoading = false;
					}
				);
			} catch (error) {
				console.error('Error en sendNotification:', error);
				this.doShowAlert({
					text: this.$t("wkt.admin.assessments.error_sending_notifications") + (error.message ? ': ' + error.message : ''),
					color: 'error',
					timeout: 4000,
				});
				this.isLoading = false;
			}
		},
		openStatisticsDialog() {
			this.isLoading = true;
			this.loadSubmissions();
		},
		onIframeLoad() {
			this.isLoading = false; // Desactiva el loader
		},
		onIframeError(error) {
			this.isLoading = false;
			console.error('Error en el iframe:', error);
		},
		doShowAlert: function (payload) {
			this.alertText = payload.text;
			this.alertColor = payload.color;
			this.alertTimeout = payload.timeout;
			this.showAlert = true;
		},

		onDownloadAssessmentExcel(assessmentType) {
			// nombre del informe
			let fileNameReport = this.assessment?.name + " - ";
			if (assessmentType === 0) {
				fileNameReport += "Autoevaluacion";
			} else if (assessmentType === 1) {
				fileNameReport += "Evaluación final";
			} else if (assessmentType === 2) {
				fileNameReport += "Autoevaluacion y Evaluación final";
			} else {
				fileNameReport += "Evaluacion";
			}
			fileNameReport += " - " + this.assessment?.id + ".xlsx";
			
			gwt.downloadFile(
				`/report/assessmentsExcel/${this.assessment?.id}/${assessmentType}`,
				fileNameReport
			);
		},
		updateSort(column) {
			this.sortBy = column;

			this.items.sort((a, b) => {
				let valA, valB;

				if (column === 'employee') {
					// Ordenación por nombre del empleado
					valA = `${a.employeeFirstname || ''} ${a.employeeLastname || ''}`.toLowerCase();
					valB = `${b.employeeFirstname || ''} ${b.employeeLastname || ''}`.toLowerCase();
				} else if (column === 'evaluator') {
					// Ordenación por nombre del evaluador
					valA = `${a.evaluatorFirstname || ''} ${a.evaluatorLastname || ''}`.toLowerCase();
					valB = `${b.evaluatorFirstname || ''} ${b.evaluatorLastname || ''}`.toLowerCase();
				} else if (column === 'phase1') {
					// Ordenación por fecha de autoevaluación
					valA = a.phase1CompletionDate ? new Date(a.phase1CompletionDate).getTime() : 0;
					valB = b.phase1CompletionDate ? new Date(b.phase1CompletionDate).getTime() : 0;
				} else if (column === 'phase2') {
					// Ordenación por fecha de evaluación final
					valA = a.phase2CompletionDate ? new Date(a.phase2CompletionDate).getTime() : 0;
					valB = b.phase2CompletionDate ? new Date(b.phase2CompletionDate).getTime() : 0;
				} else if (column === 'reviewstatus') {
					// Ordenación por estado (considera números para estados específicos)
					valA = a.reviewStatus || 0;
					valB = b.reviewStatus || 0;
				} else if (column === 'reviewscore') {
					// Ordenación por calificación
					valA = a.reviewScore || 0;
					valB = b.reviewScore || 0;
				} else {
					// Ordenación genérica
					valA = a[column] || '';
					valB = b[column] || '';
				}

				// Comparación genérica para orden ascendente/descendente
				if (valA < valB) return this.sortDesc ? 1 : -1;
				if (valA > valB) return this.sortDesc ? -1 : 1;
				return 0;
			});
		},

		updateSortDesc(isDescending) {
			this.sortDesc = isDescending;
			this.updateSort(this.sortBy);
		},
		updateEvaluatorOptions(newEvaluators) {
			newEvaluators.forEach(evaluator => {
				if (!this.evaluatorOptions.some(e => e.id === evaluator.id)) {
					this.evaluatorOptions.push(evaluator);
				}
			});
		}
	},
	mounted() {
		if (!this.tab) {
			this.applyDefinition();
		}
	},
	watch: {
		options: {
			handler() {
				this.getItems();
			},
			deep: true,
		},
	},
};
</script>
